import moment from "moment";
import PostServiceApi from "../../../../services/post-service";
import { isArray } from "lodash";
import { MAXIMUM_AGE_LIMIT } from "../../../../config/constant";

export const NOTIFICATION_PREFERENCE_ITEM_STATUS = Object.freeze({
  NONE: "NONE",
  UPDATED: "UPDATED",
  SAVING: "SAVING",
  SAVED: "SAVED",
  ERROR: "ERROR",
});

export const RESTRICTION_STATUS = Object.freeze({
  ALLOWED: "ALLOWED",
  WARNING: "WARNING",
  BLOCKED: "BLOCKED",
});

export const RESTRICTION_MESSAGES = Object.freeze({
  [RESTRICTION_STATUS.ALLOWED]: "",
  [RESTRICTION_STATUS.WARNING]:
    "* This user will turn 60 within the selected package duration.",
  [RESTRICTION_STATUS.BLOCKED]:
    "* This ad cannot be published because the user's age is 60 or above.",
});

class PageDomain {
  static getAllowedNotificationPreferences = async ({
    memberId,
    setAllowedPerferences,
  }) => {
    try {
      const response = await PostServiceApi.getAllowedPushNotificationTypes({
        memberId,
      });
      if (!response.success) {
        return;
      }
      const ignoredTypes = [
        "sub_general",
        "sub_critical",
        "promo_general",
        "promo_critical",
      ];
      const filteredPreferenceTypes = isArray(response.body)
        ? response.body.filter(
            (element) => !ignoredTypes.includes(element.notification.id)
          )
        : [];
      setAllowedPerferences(filteredPreferenceTypes);
    } catch (error) {}
  };

  static getNotificationItemUIMeta = ({ notification }) => {
    const output = { label: "", description: "" };
    switch (notification.id) {
      case "critical":
      default:
        output.label = "critical notifications";
        output.description =
          "Registration, Ad submission & registration payment related notifications";
        break;
      case "general":
        output.label = "general notifications";
        output.description = "Ad disable, Hide & Boost Ad notifications";
        break;
      case "match":
        output.label = "match updates";
        output.description =
          "Interest received/Sent/Match & other match related notifications";
        break;
      case "payment":
        output.label = "Payment notifications";
        output.description =
          "Bank details & other payment related notifications";
        break;
      case "promo_critical":
        output.label = "critical promo notifications";
        output.description = "critical promotional releated notifications";
        break;
      case "promo_general":
        output.label = "general promo notifications";
        output.description = "general promotional releated notifications";
        break;
      case "sub_critical":
        output.label = "sub critical notifications";
        output.description = "sub critical notitification";
        break;
      case "sub_general":
        output.label = "sub general notification";
        output.description = "sub general notification";
        break;
    }
    return output;
  };

  static updatePushNotificationPreference = async ({
    memberId,
    newValue,
    notification,
    setItemStatus,
    getUserAccount,
    setError,
  }) => {
    try {
      setItemStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVING);
      const response = await PostServiceApi.updatePushNotificationPreference({
        memberId,
        typeId: notification.id,
        optionId: newValue,
      });
      if (!response.success) {
        setError(response.body.message ?? "unable to update");
        return;
      }
      setError("");
      setTimeout(() => {
        setItemStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVED);
        setTimeout(() => {
          getUserAccount(memberId);
          setItemStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE);
        }, 1000);
      }, 2000);
    } catch (error) {
      console.log(">>>> ERROR ", error);
    }
  };

  static updateAccountPreference = async ({
    field,
    value,
    setItemStatus,
    getUserAccount,
    memberId,
    setError,
  }) => {
    try {
      setItemStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVING);
      const response = await PostServiceApi.updateAccountPreferences({
        memberId,
        data: { [field]: value },
      });
      if (!response.success) {
        setError(response.body.message ?? "unable to update");
        return;
      }
      setError("");
      setTimeout(() => {
        setItemStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.SAVED);
        setTimeout(() => {
          getUserAccount(memberId);
          setItemStatus(NOTIFICATION_PREFERENCE_ITEM_STATUS.NONE);
        }, 1000);
      }, 2000);
    } catch (error) {}
  };

  static hasReachedMaxAgeLimit = ({
    dateOfBirth,
    ageLimit = MAXIMUM_AGE_LIMIT,
  }) => {
    if (
      typeof dateOfBirth !== "string" ||
      !moment(dateOfBirth, "YYYY-MM-DD", true).isValid()
    ) {
      return false;
    }
    const today = moment();
    const dateWhenTurns60 = moment(dateOfBirth, "YYYY-MM-DD").add(
      ageLimit,
      "years"
    );
    return today.isSameOrAfter(dateWhenTurns60);
  };

  static willReachMaxAgeBeforePackageEnds = ({
    dateOfBirth,
    selectedPackageDuration,
  }) => {
    if (
      typeof dateOfBirth !== "string" ||
      !selectedPackageDuration ||
      !moment(dateOfBirth, "YYYY-MM-DD", true).isValid()
    ) {
      return false;
    }
    const today = moment();
    const packageEndDate = today.clone().add(selectedPackageDuration, "months");
    const dateWhenTurns60 = moment(dateOfBirth, "YYYY-MM-DD").add(
      MAXIMUM_AGE_LIMIT,
      "years"
    );
    return dateWhenTurns60.isBefore(packageEndDate);
  };

  static getRestrictionStatus = ({
    dateOfBirth,
    pricingPackages,
    selectedPackageId,
  }) => {
    const hasReachedMaxAgeLimit = PageDomain.hasReachedMaxAgeLimit({
      dateOfBirth,
    });
    if (hasReachedMaxAgeLimit) {
      return RESTRICTION_STATUS.BLOCKED;
    }

    const selectedPackage = pricingPackages.find(
      (item) => item.id === selectedPackageId
    );

    const selectedPackageDuration = selectedPackage?.durationMonths ?? "";
    const willReachMaxAgeBeforePackageEnd =
      PageDomain.willReachMaxAgeBeforePackageEnds({
        dateOfBirth,
        selectedPackageDuration,
      });

    if (willReachMaxAgeBeforePackageEnd) {
      return RESTRICTION_STATUS.WARNING;
    }

    return RESTRICTION_STATUS.ALLOWED;
  };
}
export default PageDomain;
